/**
 * cart-drawer.js
 * Slide-out cart drawer for Hattie.
 *
 * Public API:
 *   openCartDrawer()
 *   closeCartDrawer()
 *   refreshCartDrawer()                    → Promise<void>
 *   addToCart(variantId, quantity, properties) → Promise<void>
 *   changeQuantity(lineItemKey, quantity)  → Promise<void>
 *   removeItem(lineItemKey)                → Promise<void>
 *
 * Dependencies (globals from global.js):
 *   focusTrap(element)
 *   releaseFocusTrap()
 *   announceToScreenReader(message)
 *
 * Custom events dispatched (consumed by site-header.js):
 *   hattie:cart-opened
 *   hattie:cart-closed
 *   hattie:cart-updated  { detail: { itemCount: number } }
 *
 * HTML contract:
 *   #cart-drawer                — drawer root, hidden attribute toggled
 *   .cart-drawer__panel         — panel element, focus trap target
 *   #cart-drawer-body           — replaced on every cart mutation
 *   .cart-drawer__count         — item count span in drawer header
 *   .cart-drawer__subtotal      — subtotal span in drawer footer
 *   [data-cart-open]            — opens the drawer (e.g. header cart button)
 *   [data-cart-close]           — closes the drawer (e.g. panel close button)
 *   [data-overlay="cart"]       — backdrop overlay
 *   [data-cart-change]          — quantity buttons; also needs:
 *                                   data-cart-item-key, data-cart-quantity
 *   [data-cart-remove]          — remove buttons; also needs:
 *                                   data-cart-item-key, data-cart-item-title
 */

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let _drawer = null;
let _panel = null;
let _triggerEl = null;
let _isOpen = false;
let _isClosing = false;
const TRANSITION_FALLBACK_MS = 500;

function _allowsMotion() {
  return typeof prefersReducedMotion !== 'function' || !prefersReducedMotion();
}

// ---------------------------------------------------------------------------
// Open / close
// ---------------------------------------------------------------------------

function openCartDrawer() {
  if (!_drawer || _isOpen || _isClosing) return;

  _isOpen = true;
  _drawer.hidden = false;

  if (_allowsMotion()) {
    _drawer.classList.remove('is-open');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        _drawer.classList.add('is-open');
      });
    });
  } else {
    _drawer.classList.add('is-open');
  }

  document.body.classList.add('has-overlay');
  if (typeof lockPageScroll === 'function') lockPageScroll();

  const overlay = document.querySelector('[data-overlay="cart"]');
  if (typeof openOverlay === 'function') {
    openOverlay(overlay);
  } else if (overlay) {
    overlay.hidden = false;
    overlay.classList.add('is-open');
  }

  focusTrap(_panel);

  const closeBtn = _panel.querySelector('[data-cart-close]');
  if (closeBtn) closeBtn.focus();

  document.dispatchEvent(new CustomEvent('hattie:cart-opened'));
}

function _finishClose() {
  const overlay = document.querySelector('[data-overlay="cart"]');

  if (overlay) {
    overlay.hidden = true;
    overlay.classList.remove('is-open');
  }

  if (_drawer) {
    _drawer.hidden = true;
    _drawer.classList.remove('is-open');
  }
  document.body.classList.remove('has-overlay');
  if (typeof unlockPageScroll === 'function') unlockPageScroll();

  releaseFocusTrap();

  document.dispatchEvent(new CustomEvent('hattie:cart-closed'));

  if (_triggerEl) {
    const triggerEl = _triggerEl;
    _triggerEl = null;
    requestAnimationFrame(() => {
      if (typeof focusWithoutScroll === 'function') {
        focusWithoutScroll(triggerEl);
      } else {
        triggerEl.focus();
      }
    });
  }

  _isOpen = false;
  _isClosing = false;
}

function closeCartDrawer() {
  if (!_drawer || !_isOpen || _isClosing) return;

  const overlay = document.querySelector('[data-overlay="cart"]');

  if (_allowsMotion()) {
    _isClosing = true;
    _drawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');

    let finished = false;
    let pending = overlay ? 2 : 1;

    const complete = () => {
      if (finished) return;
      finished = true;
      _finishClose();
    };

    const tryComplete = () => {
      pending -= 1;
      if (pending <= 0) complete();
    };

    const onTransitionEnd = (event) => {
      if (event.target === _drawer && event.propertyName === 'transform') {
        tryComplete();
      }
      if (overlay && event.target === overlay && event.propertyName === 'opacity') {
        tryComplete();
      }
    };

    _drawer.addEventListener('transitionend', onTransitionEnd);
    if (overlay) overlay.addEventListener('transitionend', onTransitionEnd);
    window.setTimeout(() => {
      _drawer.removeEventListener('transitionend', onTransitionEnd);
      if (overlay) overlay.removeEventListener('transitionend', onTransitionEnd);
      complete();
    }, TRANSITION_FALLBACK_MS);
    return;
  }

  _finishClose();
}

// ---------------------------------------------------------------------------
// Cart data refresh
// ---------------------------------------------------------------------------

async function refreshCartDrawer() {
  const response = await fetch('/?sections=cart-drawer');
  const data = await response.json();
  const parsed = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');

  // 1. Swap scrollable body
  const newBody = parsed.querySelector('#cart-drawer-body');
  const liveBody = document.getElementById('cart-drawer-body');
  if (newBody && liveBody) liveBody.innerHTML = newBody.innerHTML;

  // 2. Update item count span in drawer header (outside #cart-drawer-body)
  const newCount = parsed.querySelector('.cart-drawer__count');
  const liveCount = document.querySelector('.cart-drawer__count');
  if (newCount && liveCount) liveCount.textContent = newCount.textContent;

  // 3. Update subtotal span in drawer footer (outside #cart-drawer-body)
  const newSubtotal = parsed.querySelector('.cart-drawer__subtotal');
  const liveSubtotal = document.querySelector('.cart-drawer__subtotal');
  if (newSubtotal && liveSubtotal) liveSubtotal.textContent = newSubtotal.textContent;

  // 4. Notify site-header.js so it can update the cart button aria-label
  const countText = newCount ? newCount.textContent : '';
  const itemCount = parseInt(countText.replace(/\D+/g, ''), 10) || 0;
  document.dispatchEvent(new CustomEvent('hattie:cart-updated', { detail: { itemCount } }));
}

// ---------------------------------------------------------------------------
// Cart mutations
// ---------------------------------------------------------------------------

/**
 * Add a variant to the cart, refresh the drawer, then open it.
 * Called by product form JS.
 * @param {number|string} variantId
 * @param {number} [quantity=1]
 * @param {Object} [properties={}] Public line-item details retained at checkout.
 */
async function addToCart(variantId, quantity = 1, properties = {}) {
  try {
    const response = await _postJSON('/cart/add.js', { id: variantId, quantity, properties });

    if (!response.ok) {
      const err = await response.json();
      announceToScreenReader(err.description || 'Could not add item to cart.');
      throw new Error(err.description);
    }

    await refreshCartDrawer();
    openCartDrawer();
    announceToScreenReader('Item added to cart.');
  } catch (err) {
    // Re-throw so the product form can show its own error state.
    throw err;
  }
}

/**
 * Update the quantity of a cart line item.
 * @param {string} lineItemKey
 * @param {number} quantity
 */
function _setCartCardLoading(card, isLoading) {
  if (!card) return;
  card.classList.toggle('is-loading', isLoading);
  if (isLoading) {
    card.setAttribute('aria-busy', 'true');
  } else {
    card.removeAttribute('aria-busy');
  }
}

async function changeQuantity(lineItemKey, quantity, cardEl) {
  _setCartCardLoading(cardEl, true);

  try {
    const response = await _postJSON('/cart/change.js', { id: lineItemKey, quantity });

    if (!response.ok) {
      const err = await response.json();
      _setCartCardLoading(cardEl, false);
      announceToScreenReader(err.description || 'Could not update quantity.');
      return;
    }

    await refreshCartDrawer();
    announceToScreenReader('Quantity updated.');
  } catch (_) {
    _setCartCardLoading(cardEl, false);
    announceToScreenReader('Something went wrong. Please try again.');
  }
}

/**
 * Remove a line item from the cart.
 * @param {string} lineItemKey
 * @param {string} [productTitle=''] — used in the screen reader announcement
 */
async function removeItem(lineItemKey, productTitle = '', cardEl) {
  _setCartCardLoading(cardEl, true);

  try {
    const response = await _postJSON('/cart/change.js', { id: lineItemKey, quantity: 0 });

    if (!response.ok) {
      const err = await response.json();
      _setCartCardLoading(cardEl, false);
      announceToScreenReader(err.description || 'Could not remove item.');
      return;
    }

    await refreshCartDrawer();

    const announcement = productTitle
      ? `${productTitle} removed from cart.`
      : 'Item removed from cart.';
    announceToScreenReader(announcement);

    // Move focus to the drawer body so keyboard users are not left stranded
    // after the remove button disappears on re-render.
    const body = document.getElementById('cart-drawer-body');
    if (body) body.focus();
  } catch (_) {
    _setCartCardLoading(cardEl, false);
    announceToScreenReader('Something went wrong. Please try again.');
  }
}

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

function _postJSON(url, body) {
  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

document.addEventListener('DOMContentLoaded', () => {
  _drawer = document.getElementById('cart-drawer');
  if (!_drawer) return;

  _panel = _drawer.querySelector('.cart-drawer__panel');

  const overlay = document.querySelector('[data-overlay="cart"]');
  if (overlay) {
    overlay.addEventListener('click', closeCartDrawer);
  }

  // Open triggers — store the trigger element so focus can return on close.
  document.querySelectorAll('[data-cart-open]').forEach((el) => {
    el.addEventListener('click', (event) => {
      event.preventDefault();
      if (_isOpen) {
        _triggerEl = el;
        closeCartDrawer();
        return;
      }
      _triggerEl = el;
      openCartDrawer();
    });
  });

  // Escape key closes the drawer from anywhere on the page.
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !_drawer.hidden) {
      closeCartDrawer();
    }
  });

  // Event delegation on the drawer for close, quantity, and remove.
  // Delegation is required because #cart-drawer-body is replaced on mutations,
  // which would destroy any directly-bound listeners inside it.
  _drawer.addEventListener('click', (event) => {
    if (event.target.closest('[data-cart-close]')) {
      closeCartDrawer();
      return;
    }

    const changeBtn = event.target.closest('[data-cart-change]');
    if (changeBtn) {
      const card = changeBtn.closest('.cart-product-card');
      if (card?.classList.contains('is-loading')) return;

      const key = changeBtn.dataset.cartItemKey;
      const quantity = parseInt(changeBtn.dataset.cartQuantity, 10);
      changeQuantity(key, quantity, card);
      return;
    }

    const removeBtn = event.target.closest('[data-cart-remove]');
    if (removeBtn) {
      const card = removeBtn.closest('.cart-product-card');
      if (card?.classList.contains('is-loading')) return;

      const key = removeBtn.dataset.cartItemKey;
      const title = removeBtn.dataset.cartItemTitle || '';
      removeItem(key, title, card);
    }
  });
});
